import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import * as Cesium from 'cesium';
import { normalizeSpeciesValue } from '../utils/treeSpecies';

/**
 * Hook to manage tree popup state and interactions.
 * Extracts ~400 lines of tree popup logic from BaseCesiumViewer.
 *
 * @param {Object} viewer - The Cesium viewer instance
 * @returns {Object} Tree popup state and management methods
 */
export function useTreePopups(viewer) {
  const [treePopups, setTreePopups] = useState([]);
  const [trackedPopupId, setTrackedPopupId] = useState(null);
  const [editingPopupId, setEditingPopupId] = useState(null);
  const [editDrafts, setEditDrafts] = useState({});

  const treePopupsRef = useRef([]);

  // Keep ref in sync with state
  useEffect(() => {
    treePopupsRef.current = treePopups;
  }, [treePopups]);

  // Clear editing state when popup is removed
  useEffect(() => {
    if (editingPopupId && !treePopups.some((popup) => popup.popupId === editingPopupId)) {
      setEditingPopupId(null);
    }
  }, [editingPopupId, treePopups]);

  /**
   * Get entity by popup ID from viewer.
   */
  const getEntityByPopupId = useCallback((popupId) => {
    if (!viewer || popupId === null || popupId === undefined) return null;
    const entity = viewer.entities?.getById?.(popupId);
    if (entity && !entity.isDestroyed?.()) {
      return entity;
    }
    return null;
  }, [viewer]);

  /**
   * Compute axis from entity orientation.
   */
  const computeAxisFromOrientation = useCallback((entity) => {
    if (!viewer || !entity) return null;

    let position = entity.position;
    if (position?.getValue) {
      position = position.getValue(viewer.clock?.currentTime);
    }
    const orientation = entity.orientation?.getValue
      ? entity.orientation.getValue(viewer.clock?.currentTime)
      : entity.orientation;

    if (!position || !orientation) return null;

    try {
      const rotation = Cesium.Matrix3.fromQuaternion(orientation);
      const worldAxis = Cesium.Matrix3.multiplyByVector(
        rotation,
        Cesium.Cartesian3.UNIT_Z,
        new Cesium.Cartesian3()
      );
      if (!worldAxis || !Cesium.Cartesian3.magnitude(worldAxis)) return null;

      const enu = Cesium.Transforms.eastNorthUpToFixedFrame(position);
      const inverseEnu = Cesium.Matrix4.inverse(enu, new Cesium.Matrix4());
      if (!inverseEnu) return null;

      const localAxis = Cesium.Matrix4.multiplyByPointAsVector(
        inverseEnu,
        worldAxis,
        new Cesium.Cartesian3()
      );
      if (!localAxis || !Cesium.Cartesian3.magnitude(localAxis)) return null;

      return Cesium.Cartesian3.normalize(localAxis, localAxis);
    } catch (error) {
      console.warn('Failed to derive cylinder axis:', error);
      return null;
    }
  }, [viewer]);

  /**
   * Apply updates to a popup.
   */
  const applyPopupUpdates = useCallback((popupId, updates) => {
    setTreePopups((prev) => prev.map((popup) => (
      popup.popupId === popupId
        ? { ...popup, ...updates }
        : popup
    )));
  }, []);

  /**
   * Update tree info metadata on entity.
   */
  const updateTreeInfoMetadata = useCallback((popupId, updates) => {
    if (!updates || !Object.keys(updates).length) return;
    const entity = getEntityByPopupId(popupId);
    if (entity?.treeInfo) {
      Object.entries(updates).forEach(([key, value]) => {
        entity.treeInfo[key] = value;
        if (entity.treeInfo.properties && typeof entity.treeInfo.properties === 'object') {
          if (key === 'axis' && value && typeof value === 'object') {
            entity.treeInfo.properties.dx = value.x;
            entity.treeInfo.properties.dy = value.y;
            entity.treeInfo.properties.dz = value.z;
          } else if (key === 'lon') {
            entity.treeInfo.properties.center_lon = value;
          } else if (key === 'lat') {
            entity.treeInfo.properties.center_lat = value;
          } else if (key === 'elevation') {
            entity.treeInfo.properties.center_z = value;
          } else {
            entity.treeInfo.properties[key] = value;
          }
        }
      });
    }
  }, [getEntityByPopupId]);

  /**
   * Update cylinder parameters (radius/height) on entity.
   */
  const updateCylinderParameters = useCallback((popupId, { radius, height }) => {
    const entity = getEntityByPopupId(popupId);
    if (!entity || entity.isDestroyed?.()) return;

    if (entity.cylinder) {
      if (Number.isFinite(radius) && radius > 0) {
        entity.cylinder.topRadius = radius;
        entity.cylinder.bottomRadius = radius;
      }
      if (Number.isFinite(height) && height > 0) {
        entity.cylinder.length = height;
      }
    }

    if (entity.treeInfo) {
      if (Number.isFinite(radius)) {
        entity.treeInfo.radius = radius;
        if (entity.treeInfo.properties) {
          entity.treeInfo.properties.radius = radius;
        }
      }
      if (Number.isFinite(height)) {
        entity.treeInfo.height = height;
        if (entity.treeInfo.properties) {
          entity.treeInfo.properties.height = height;
        }
      }
    }

    const updates = {};
    if (Number.isFinite(radius)) updates.radius = radius;
    if (Number.isFinite(height)) updates.height = height;
    if (Object.keys(updates).length) {
      applyPopupUpdates(popupId, updates);
    }
  }, [getEntityByPopupId, applyPopupUpdates]);

  /**
   * Close a tree popup.
   */
  const closeTreePopup = useCallback((popupId) => {
    setTreePopups((prev) => prev.filter((popup) => popup.popupId !== popupId));
    setEditingPopupId((prev) => (prev === popupId ? null : prev));
    setEditDrafts((prev) => {
      if (!prev[popupId]) return prev;
      const next = { ...prev };
      delete next[popupId];
      return next;
    });
    setTrackedPopupId((prev) => {
      if (prev === popupId) {
        if (viewer) {
          viewer.trackedEntity = undefined;
        }
        return null;
      }
      return prev;
    });
  }, [viewer]);

  /**
   * Delete tree popup and its associated entity.
   */
  const deleteTreePopupAndEntity = useCallback((popupId) => {
    const entity = getEntityByPopupId(popupId);
    if (entity && viewer?.entities) {
      try {
        viewer.entities.remove(entity);
      } catch (error) {
        console.warn('Failed to remove tree entity:', error);
      }
    }
    closeTreePopup(popupId);
  }, [viewer, getEntityByPopupId, closeTreePopup]);

  /**
   * Toggle camera tracking for a popup.
   */
  const toggleTrackedPopup = useCallback((popupId) => {
    if (!viewer) return;

    setTrackedPopupId((prevTrackedId) => {
      if (prevTrackedId === popupId) {
        viewer.trackedEntity = undefined;
        return null;
      }

      const entity = viewer.entities?.getById?.(popupId);
      if (entity && !entity.isDestroyed?.() && entity.show !== false) {
        viewer.trackedEntity = entity;
        return popupId;
      }

      viewer.trackedEntity = undefined;
      return null;
    });
  }, [viewer]);

  /**
   * Build draft data for a popup.
   */
  const buildDraftForPopup = useCallback((popup) => {
    if (!popup) return { treeId: '', radius: '', height: '', comment: '', species: '' };
    const entity = getEntityByPopupId(popup.popupId);
    const radius = popup.radius ?? entity?.treeInfo?.radius ?? entity?.cylinder?.topRadius ?? '';
    const height = popup.height ?? entity?.treeInfo?.height ?? '';
    const treeId = popup.treeId ?? entity?.treeInfo?.id ?? '';
    const comment = popup.comment
      ?? entity?.treeInfo?.properties?.comment
      ?? entity?.treeInfo?.comment
      ?? '';
    const species = normalizeSpeciesValue(
      popup.species
      ?? entity?.treeInfo?.properties?.species
      ?? entity?.treeInfo?.species
    );
    return { treeId, radius, height, comment, species };
  }, [getEntityByPopupId]);

  /**
   * Toggle edit mode for a popup.
   */
  const togglePopupEditMode = useCallback((popup) => {
    if (!popup) return;
    const isEnabling = editingPopupId !== popup.popupId;
    if (isEnabling) {
      const draft = buildDraftForPopup(popup);
      setEditDrafts((prev) => ({ ...prev, [popup.popupId]: draft }));
    }
    setEditingPopupId((prev) => (prev === popup.popupId ? null : popup.popupId));
  }, [editingPopupId, buildDraftForPopup]);

  /**
   * Handle edit field changes.
   */
  const handleEditFieldChange = useCallback((popupId, field, value) => {
    if (field === 'treeId') {
      // Tree ID is read-only; ignore edits
      return;
    }

    setEditDrafts((prev) => ({
      ...prev,
      [popupId]: { ...(prev[popupId] || {}), [field]: value }
    }));

    if (field === 'comment') {
      applyPopupUpdates(popupId, { comment: value ?? '' });
      updateTreeInfoMetadata(popupId, { comment: value ?? '' });
      return;
    }

    if (field === 'species') {
      const normalized = normalizeSpeciesValue(value);
      applyPopupUpdates(popupId, { species: normalized });
      updateTreeInfoMetadata(popupId, { species: normalized });
      return;
    }

    const numericValue = parseFloat(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return;
    }

    if (field === 'radius') {
      updateCylinderParameters(popupId, { radius: numericValue });
    } else if (field === 'height') {
      applyPopupUpdates(popupId, { height: numericValue });
      updateTreeInfoMetadata(popupId, { height: numericValue });
    }
  }, [applyPopupUpdates, updateCylinderParameters, updateTreeInfoMetadata]);

  /**
   * Handle radius step changes (increment/decrement).
   */
  const handleRadiusStep = useCallback((popupId, deltaMeters = 0.01) => {
    let baseRadius = Number.parseFloat(editDrafts[popupId]?.radius);
    if (!Number.isFinite(baseRadius)) {
      const popup = treePopupsRef.current.find((p) => p.popupId === popupId);
      baseRadius = Number.isFinite(popup?.radius) ? popup.radius : null;
    }
    if (!Number.isFinite(baseRadius) || baseRadius <= 0) {
      return;
    }
    const next = Math.max(0.01, baseRadius + deltaMeters);
    const rounded = Math.round(next * 100) / 100; // keep 1 cm precision

    setEditDrafts((prev) => ({
      ...prev,
      [popupId]: { ...(prev[popupId] || {}), radius: rounded }
    }));
    updateCylinderParameters(popupId, { radius: rounded });
  }, [editDrafts, updateCylinderParameters]);

  /**
   * Handle tree entity click to open popup.
   */
  const handleTreeEntityClick = useCallback((position, screenPosition) => {
    if (!viewer) return;

    const picked = viewer.scene.pick(screenPosition);
    if (!picked || !picked.id || !picked.id.treeInfo) return;

    const entity = picked.id;
    const treeInfo = entity.treeInfo;
    const popupId = treeInfo.entityId || entity.id || treeInfo.id;
    const axisVector = treeInfo.axis
      ? new Cesium.Cartesian3(treeInfo.axis.x, treeInfo.axis.y, treeInfo.axis.z)
      : computeAxisFromOrientation(entity);
    const axis = axisVector
      ? { x: axisVector.x, y: axisVector.y, z: axisVector.z }
      : null;
    if (axis && !treeInfo.axis) {
      treeInfo.axis = axis;
    }

    setTreePopups((prev) => {
      if (prev.some((popup) => popup.popupId === popupId)) {
        return prev;
      }

      let worldPosition = entity.position;
      if (entity.position && typeof entity.position.getValue === 'function') {
        worldPosition = entity.position.getValue(viewer.clock.currentTime);
      }
      worldPosition = worldPosition || position;

      if (!worldPosition) {
        return prev;
      }

      const canvasPosition = viewer.scene.cartesianToCanvasCoordinates(
        worldPosition,
        new Cesium.Cartesian2()
      );

      return [
        ...prev,
        {
          popupId,
          treeId: treeInfo.id,
          lon: treeInfo.lon,
          lat: treeInfo.lat,
          elevation: treeInfo.elevation,
          radius: treeInfo.radius,
          height: treeInfo.height,
          species: normalizeSpeciesValue(treeInfo.properties?.species ?? treeInfo.species),
          comment: treeInfo.properties?.comment ?? treeInfo.comment ?? '',
          axis,
          worldPosition: Cesium.Cartesian3.clone(worldPosition),
          screenPosition: canvasPosition ? { x: canvasPosition.x, y: canvasPosition.y } : null
        }
      ];
    });
  }, [viewer, computeAxisFromOrientation]);

  /**
   * Update screen positions of all popups.
   */
  const updateTreePopupScreens = useCallback(() => {
    if (!viewer) return;

    setTreePopups((prev) => {
      let changed = false;
      const next = [];

      prev.forEach((popup) => {
        if (!popup) return;

        const entity = viewer.entities?.getById?.(popup.popupId);
        if (!entity || entity.isDestroyed?.() || entity.show === false) {
          changed = true;
          return;
        }

        if (!popup.worldPosition) {
          next.push(popup);
          return;
        }

        const canvasPosition = viewer.scene.cartesianToCanvasCoordinates(
          popup.worldPosition,
          new Cesium.Cartesian2()
        );

        if (!canvasPosition) {
          next.push(popup);
          return;
        }

        const samePosition = popup.screenPosition
          && popup.screenPosition.x === canvasPosition.x
          && popup.screenPosition.y === canvasPosition.y;

        if (samePosition) {
          next.push(popup);
          return;
        }

        changed = true;
        next.push({
          ...popup,
          screenPosition: { x: canvasPosition.x, y: canvasPosition.y }
        });
      });

      return changed ? next : prev;
    });
  }, [viewer]);

  // Sync tracked entity with viewer when trackedPopupId changes
  useEffect(() => {
    if (!trackedPopupId || !viewer) return;

    const popupExists = treePopups.some((popup) => popup.popupId === trackedPopupId);
    if (!popupExists) {
      viewer.trackedEntity = undefined;
      setTrackedPopupId(null);
      return;
    }

    const entity = viewer.entities?.getById?.(trackedPopupId);
    if (entity && !entity.isDestroyed?.() && entity.show !== false) {
      viewer.trackedEntity = entity;
    } else {
      viewer.trackedEntity = undefined;
      setTrackedPopupId(null);
    }
  }, [trackedPopupId, treePopups, viewer]);

  // Computed view models for rendering
  const treePopupViewModels = useMemo(() => (
    treePopups.map((popup) => {
      const isEditing = editingPopupId === popup.popupId;
      const draft = isEditing
        ? (editDrafts[popup.popupId] || buildDraftForPopup(popup))
        : null;

      return {
        ...popup,
        isEditing,
        draft
      };
    })
  ), [treePopups, editingPopupId, editDrafts, buildDraftForPopup]);

  return {
    // State
    treePopups,
    trackedPopupId,
    editingPopupId,
    editDrafts,

    // Setters (for direct access if needed)
    setTreePopups,
    setTrackedPopupId,
    setEditingPopupId,
    setEditDrafts,

    // Refs
    treePopupsRef,

    // Core methods
    closeTreePopup,
    deleteTreePopupAndEntity,
    toggleTrackedPopup,
    togglePopupEditMode,
    handleEditFieldChange,
    handleRadiusStep,
    handleTreeEntityClick,
    updateTreePopupScreens,

    // Helper methods
    getEntityByPopupId,
    computeAxisFromOrientation,
    applyPopupUpdates,
    updateTreeInfoMetadata,
    updateCylinderParameters,
    buildDraftForPopup,

    // Computed
    treePopupViewModels,
  };
}

export default useTreePopups;
